import {
  type ClockSchedule,
  clockDaysOf,
  clockOffsetAt,
  clockSchedule,
  type DayAdjustment,
  type DayTimeline,
  dayTimeline,
  type ShipDay,
  type ShipTime,
  wallInstants,
  wallLabel,
} from '@felix/domain';

/**
 * Arayüzde gemi saati (ADR 0065 §4, §7): ekranlar geminin dilimini ya da saat çizelgesini alır.
 * Hesap `@felix/domain` `clock-log`'dadır; burada çizelge, tablo ve aylık ızgara için gösterim
 * yardımcıları vardır. Saf fonksiyonlar.
 */

const MINUTE = 60_000;

/** Gemi saati: IANA dilimi (saat kaydı yoksa) ya da saat çizelgesi (`@felix/domain`). */
export type { ShipTime };

export function asClock(time: ShipTime): ClockSchedule {
  return typeof time === 'string' ? clockSchedule(time) : time;
}

/**
 * Geminin saati: sunucunun verdiği saat çizelgesi (`clock`; ADR 0065 §4), yoksa geminin dilimi.
 * Ekranlar gemi saatini (varsayılan, çeviri, "bugün") bununla hesaplar.
 */
export function vesselTime(
  vessel: { timeZone?: string | null; clock?: ClockSchedule | null } | null | undefined,
  fallback = 'UTC',
): ShipTime {
  return vessel?.clock ?? vessel?.timeZone ?? fallback;
}

/** Saat kaydının bir satırı (gemi ve ofis aynı biçim; `packages/db` `ClockChangeView`). */
export interface ClockChangeView {
  id: string;
  effectiveAt: string;
  oldOffsetMin: number;
  newOffsetMin: number;
  changeMin: number;
  reason: string;
  status: 'pending' | 'approved' | 'cancelled';
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  approvedBy: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  cancelledBy: string | null;
  cancelledByName: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
}

/** Dilim ve onaylı değişikliklerden saat çizelgesi; onay bekleyen ve iptal hesaba girmez (§3). */
export function shipClockOf(
  timeZone: string,
  changes: readonly ClockChangeView[] | undefined,
): ClockSchedule {
  return clockSchedule(
    timeZone,
    (changes ?? []).filter((c) => c.status === 'approved'),
  );
}

/** Takvim gününün tamamı: tarih çizgisinde iki oluşum tek aralıkta; atlanan gün boş. */
export interface DaySpan {
  date: string;
  start: number;
  end: number;
  minutes: number;
  skipped: boolean;
  /** İki kez yaşanan gün (tarih çizgisi). */
  repeatedDate: boolean;
  timeline: DayTimeline;
}

export function daySpan(time: ShipTime, date: string): DaySpan {
  const clock = asClock(time);
  const days = clockDaysOf(clock, date);
  const first = days[0] as ShipDay;
  const last = days.at(-1) as ShipDay;
  const start = Date.parse(first.start);
  const end = Date.parse(last.end);
  const merged: ShipDay = {
    date,
    start: first.start,
    end: last.end,
    minutes: Math.round((end - start) / MINUTE),
    ...(first.skipped ? { skipped: true as const } : {}),
  };
  return {
    date,
    start,
    end,
    minutes: merged.minutes,
    skipped: first.skipped === true,
    repeatedDate: days.length > 1,
    timeline: dayTimeline(clock, merged),
  };
}

/** Gün saat kaydıyla olağan mı: tek parça, 24 saat (eski ekran düzeni aynen kalır). */
export function isPlainDay(span: DaySpan): boolean {
  return (
    span.minutes === 1440 &&
    span.timeline.adjustments.length === 0 &&
    span.timeline.skipped.length === 0
  );
}

const pad = (value: number) => String(value).padStart(2, '0');
/** Dakikadan `SS:DD` (gün içi; 1440 → "24:00"). */
export function clockText(minutes: number): string {
  const m = ((minutes % 1440) + 1440) % 1440;
  const value = minutes > 0 && m === 0 ? 1440 : m;
  return `${pad(Math.floor(value / 60))}:${pad(value % 60)}`;
}

/** Anın duvar saati: `SS:DD` ve tekrar eden saatse oluşumu (1./2.). */
export function wallClock(
  time: ShipTime,
  at: number,
): { date: string; time: string; occurrence: 1 | 2 | null } {
  const label = wallLabel(asClock(time), at);
  return { date: label.date, time: clockText(label.minute), occurrence: label.occurrence };
}

/** Günün bir gerçek dakikasının duvar dakikası (gün tarihinin gece yarısından; 25 saatlik günde
 * 24:00 = 1440). */
export function wallMinute(span: DaySpan, real: number): number {
  const pieces = span.timeline.pieces;
  let piece = pieces[0];
  for (const p of pieces) if (p.from <= real) piece = p;
  if (!piece) return real;
  return piece.wallFrom + (real - piece.from);
}

/** Çizelgenin bir bloğu: gerçek zaman (`time`) ya da yaşanmayan duvar saati (`skipped`). */
export interface AxisBlock {
  kind: 'time' | 'skipped';
  /** Gerçek dakika (gün başından); atlanan blokta ikisi aynı. */
  from: number;
  to: number;
  wallFrom: number;
  wallTo: number;
  /** Eksendeki başlangıç ve genişlik (gösterim dakikası). */
  at: number;
  width: number;
}

export interface DayAxis {
  /** Gösterim uzunluğu: gerçek süre + atlanan duvar saati. */
  length: number;
  blocks: AxisBlock[];
  /** Saat etiketleri: gösterim konumu, duvar saati, oluşum. */
  ticks: { at: number; wall: number; occurrence: 1 | 2 | null }[];
  /** "Clock adjustment" işaretleri. */
  markers: (DayAdjustment & { position: number })[];
  /** İkinci kez yaşanan aralıklar (gösterim konumu). */
  repeated: { at: number; width: number; wallFrom: number; wallTo: number }[];
}

/**
 * Günün ekseni (§7): gerçek süre parçaları; ileri almada yaşanmayan duvar saati kendi
 * genişliğinde taralı blok olarak araya girer; geri almada tekrar eden dilim ikinci kez yer alır.
 * Olağan günde tek blok, 0–1440, iki saatte bir etiket (eski ekranla aynı).
 */
export function dayAxis(span: DaySpan, step = 120): DayAxis {
  const { pieces, skipped, adjustments, repeated } = span.timeline;
  const blocks: AxisBlock[] = [];
  let cursor = 0;
  const gapsAt = (real: number) => skipped.filter((g) => g.at === real);
  pieces.forEach((piece) => {
    for (const gap of gapsAt(piece.from)) {
      const width = gap.wallTo - gap.wallFrom;
      blocks.push({
        kind: 'skipped',
        from: piece.from,
        to: piece.from,
        wallFrom: gap.wallFrom,
        wallTo: gap.wallTo,
        at: cursor,
        width,
      });
      cursor += width;
    }
    const length = piece.to - piece.from;
    if (length > 0) {
      blocks.push({
        kind: 'time',
        from: piece.from,
        to: piece.to,
        wallFrom: piece.wallFrom,
        wallTo: piece.wallFrom + length,
        at: cursor,
        width: length,
      });
      cursor += length;
    }
  });
  // Günün sonundaki atlanan duvar saati (ör. 23:30'da ileri alma)
  const lastReal = pieces.at(-1)?.to ?? 0;
  for (const gap of skipped.filter((g) => g.at === lastReal && lastReal > 0)) {
    if (blocks.some((b) => b.kind === 'skipped' && b.wallFrom === gap.wallFrom)) continue;
    const width = gap.wallTo - gap.wallFrom;
    blocks.push({
      kind: 'skipped',
      from: lastReal,
      to: lastReal,
      wallFrom: gap.wallFrom,
      wallTo: gap.wallTo,
      at: cursor,
      width,
    });
    cursor += width;
  }
  const position = (real: number) => displayMinute(blocks, real);
  // Etiketler (bant dar: çakışmasınlar): önce zaman bloklarının başları (geri almada
  // "23:00 (2.)", ileri almada saatin devam ettiği "01:00"), sonra gün sonu, sonra yaşanmayan
  // bloğun başı; birbirine 90 dakikadan yakın olan atlanır. İki saatlik adım etiketi gün içindeki
  // bir blok başına 150, kenarlara 90 dakikadan yakınsa atlanır. Olağan günde 00:00…24:00, 13 etiket.
  const accepted: (DayAxis['ticks'][number] & { edge: boolean })[] = [];
  const accept = (tick: DayAxis['ticks'][number], gap: number) => {
    if (accepted.some((t) => Math.abs(t.at - tick.at) < gap)) return;
    accepted.push({ ...tick, edge: tick.at === 0 || tick.at === cursor });
  };
  for (const block of blocks) {
    if (block.kind !== 'time') continue;
    accept(
      {
        at: block.at,
        wall: block.wallFrom,
        occurrence: occurrenceOf(span, block, block.wallFrom),
      },
      90,
    );
  }
  const last = blocks.at(-1);
  if (last) accept({ at: cursor, wall: last.wallTo, occurrence: null }, 90);
  for (const block of blocks) {
    if (block.kind === 'skipped')
      accept({ at: block.at, wall: block.wallFrom, occurrence: null }, 90);
  }
  const specials = [...accepted];
  for (const block of blocks) {
    if (block.kind !== 'time') continue;
    const firstTick = Math.ceil(block.wallFrom / step) * step;
    for (let wall = firstTick; wall < block.wallTo; wall += step) {
      const at = block.at + (wall - block.wallFrom);
      const crowded = specials.some(
        (t) => t.at !== at && Math.abs(t.at - at) < (t.edge ? 90 : 150),
      );
      if (crowded || accepted.some((t) => t.at === at)) continue;
      accepted.push({ at, wall, occurrence: occurrenceOf(span, block, wall), edge: false });
    }
  }
  const byPosition = new Map(accepted.map(({ edge: _edge, ...tick }) => [tick.at, tick] as const));
  const ticks = [...byPosition.values()].sort((a, b) => a.at - b.at);
  return {
    length: cursor,
    blocks,
    ticks,
    markers: adjustments.map((a) => ({
      ...a,
      // İleri almada işaret saatin devam ettiği yerde (taralı bloğun sonunda)
      position: position(a.at),
    })),
    repeated: repeatedSlices(span, blocks, repeated),
  };
}

/** Gerçek dakikanın eksendeki konumu (önündeki atlanan bloklar eklenir). */
export function displayMinute(blocks: readonly AxisBlock[], real: number): number {
  let position = real;
  for (const block of blocks) {
    if (block.kind === 'skipped' && block.from <= real) position += block.width;
  }
  // Aynı gerçek dakikadaki atlanan blok (ileri alma) noktayı bloğun sonuna taşır
  return position;
}

/** Bloğun duvar dakikası tekrar eden aralıktaysa kaçıncı oluşum. */
function occurrenceOf(span: DaySpan, block: AxisBlock, wall: number): 1 | 2 | null {
  const covering = span.timeline.repeated.some((r) => wall >= r.wallFrom && wall < r.wallTo);
  if (!covering) return null;
  const earlier = span.timeline.pieces.filter(
    (p) => p.from < block.from && wall >= p.wallFrom && wall < p.wallFrom + (p.to - p.from),
  );
  return earlier.length > 0 ? 2 : 1;
}

function repeatedSlices(
  span: DaySpan,
  blocks: readonly AxisBlock[],
  repeated: DayTimeline['repeated'],
): DayAxis['repeated'] {
  const slices: DayAxis['repeated'] = [];
  for (const range of repeated) {
    // İkinci oluşum: aralığı kapsayan, önünde aynı duvar saatini yaşamış blok
    for (const block of blocks) {
      if (block.kind !== 'time') continue;
      const lo = Math.max(range.wallFrom, block.wallFrom);
      const hi = Math.min(range.wallTo, block.wallTo);
      if (hi <= lo) continue;
      if (occurrenceOf(span, block, lo) !== 2) continue;
      slices.push({
        at: block.at + (lo - block.wallFrom),
        width: hi - lo,
        wallFrom: lo,
        wallTo: hi,
      });
    }
  }
  return slices;
}

/**
 * Aylık ızgara (ILO/IMO 24 sütun, duvar saati; §7): gerçek dakikalık aralık duvar saatine
 * çevrilir; tekrar eden saatin iki oluşumu aynı sütunda birleşir. 0–1440'a kırpılır.
 */
export function wallRanges(
  span: DaySpan,
  ranges: readonly { from: number; to: number }[],
): { from: number; to: number }[] {
  const out: { from: number; to: number }[] = [];
  for (const range of ranges) {
    for (const piece of span.timeline.pieces) {
      const lo = Math.max(range.from, piece.from);
      const hi = Math.min(range.to, piece.to);
      if (hi <= lo) continue;
      const from = Math.max(0, piece.wallFrom + (lo - piece.from));
      const to = Math.min(1440, piece.wallFrom + (hi - piece.from));
      if (to > from) out.push({ from, to });
    }
  }
  out.sort((a, b) => a.from - b.from);
  const merged: { from: number; to: number }[] = [];
  for (const r of out) {
    const last = merged.at(-1);
    if (last && r.from <= last.to) last.to = Math.max(last.to, r.to);
    else merged.push({ ...r });
  }
  return merged;
}

/** Duvar saati yerel tarih ve dakikadan an(lar): yaşanmadıysa boş, tekrar ediyorsa iki. */
export function wallToInstants(time: ShipTime, date: string, minute: number): number[] {
  return wallInstants(asClock(time), date, minute);
}

/** Anın gemi saati farkı (dakika). */
export function offsetAt(time: ShipTime, at: number): number {
  return clockOffsetAt(asClock(time), at);
}
